import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { PageSummaryDto } from '../../../shared/api.js';
import { api } from '../lib/api';
import { formatShortDateTime } from '../lib/format';

function CopyLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard
      ?.writeText(url)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => undefined);
  };
  return (
    <button type="button" className="btn btn--small btn--ghost" onClick={copy}>
      {copied ? 'Скопировано' : 'Копировать ссылку'}
    </button>
  );
}

function size(bytes: number): string {
  return bytes < 1024 ? `${bytes} Б` : `${Math.round(bytes / 1024)} КБ`;
}

function PageRow({ page, canDelete }: { page: PageSummaryDto; canDelete: boolean }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api.deletePage(page.key),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['pages'] }),
  });
  return (
    <li className="page-row">
      <div className="page-row__main">
        <a href={page.url} target="_blank" rel="noreferrer" className="page-row__title">
          {page.title}
        </a>
        <span className="muted small">
          {formatShortDateTime(page.createdAt)}
          {page.createdBy && ` · ${page.createdBy.replace(/^(user|local):/, '')}`}
          {` · ${page.format === 'html' ? 'HTML' : 'Markdown'}, ${size(page.size)}`}
          {` · просмотров: ${page.views}`}
        </span>
        {remove.isError && <span className="text-error small">{remove.error.message}</span>}
      </div>
      <div className="actions-row">
        <CopyLink url={page.url} />
        {canDelete && (
          <button
            type="button"
            className="btn btn--small btn--ghost"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(`Удалить «${page.title}»? Ссылка перестанет работать.`)) {
                remove.mutate();
              }
            }}
          >
            Удалить
          </button>
        )}
      </div>
    </li>
  );
}

/** Pages the agents published for sharing; anyone with a link opens it without signing in. */
export function Pages({ canDelete }: { canDelete: boolean }) {
  const query = useQuery({ queryKey: ['pages'], queryFn: api.pages });
  const pages = query.data?.pages ?? [];
  return (
    <div className="page">
      <div className="page__header">
        <h2>Страницы</h2>
      </div>
      <p className="muted">
        Страницы, которые агент опубликовал по вашей просьбе. Открываются по ссылке без входа —
        отправляйте её только тем, кому можно.
      </p>
      {query.isPending && <p className="muted">Загрузка…</p>}
      {query.isError && <p className="text-error">{query.error.message}</p>}
      {query.data && !query.data.available && (
        <p className="muted">Страницы недоступны: нет базы.</p>
      )}
      {query.data?.available && pages.length === 0 && (
        <p className="muted">
          Страниц пока нет. Попросите агента: «сделай страницу с … и дай ссылку».
        </p>
      )}
      {pages.length > 0 && (
        <ul className="card page-list">
          {pages.map((p) => (
            <PageRow key={p.key} page={p} canDelete={canDelete} />
          ))}
        </ul>
      )}
    </div>
  );
}
